import { useEffect, useState } from "react";
import uniqBy from "lodash-es/uniqBy";
import compact from "lodash-es/compact";

// Types
import { IPayPeriod, IStatus } from "@/Common/api/types/CommissionCycle";

type TInput = { commissionCycleId: string; payPeriodId: string; positions: string[] };

type ApproveWashoutsDialogProps = {
	commissionCycleId?: string;
	payPeriod?: IPayPeriod;
	partnerPayPeriod?: IPayPeriod | null;
	openPositionsSelector: boolean;
	positionStatuses?: IStatus[];
	disabledPositions?: string[];
	setOpenPositionsSelector: (value: boolean) => void;
	approveWashouts: (input: TInput) => void;
};

// Helpers
import { disabledPositionsRelativeToCycles, resolveDisabledPositionsByStatus } from "@/Washouts/helpers/resolveDisabledPositionsByStatus";

// Components
import PositionSelectorDialog from "../PositionSelectorDialog";

// Constants
const allowedStatuses = ["REVIEWING"];

export default function ApproveWashoutsDialog(props: Readonly<ApproveWashoutsDialogProps>) {
	const {
		openPositionsSelector,
		positionStatuses,
		disabledPositions = [],
		setOpenPositionsSelector,
		approveWashouts,
		payPeriod,
		partnerPayPeriod,
		commissionCycleId = "",
	} = props;
	const [selectedPositions, setSelectedPositions] = useState<string[]>([]);
	const [selectedPayPeriodPart, setSelectedPayPeriodPart] = useState<string[]>([]);

	const disabledPositionsByStatus = resolveDisabledPositionsByStatus({
		positionStatuses,
		allowedStatuses,
	});
	const disabledPositionsByStatusCycle = disabledPositionsRelativeToCycles({
		positionStatuses,
		allowedStatuses,
		cycles: selectedPayPeriodPart,
	});

	const finalDisabledPositions = [...new Set([...disabledPositions, ...disabledPositionsByStatus, ...disabledPositionsByStatusCycle])];

	const uniqPositionStatuses = uniqBy(positionStatuses, "position");
	const enabledPositions = uniqPositionStatuses
		.filter((item) => !finalDisabledPositions?.includes(item.position))
		.map((item) => item.position);
	const checkedAll = enabledPositions.length === selectedPositions.length;

	const handleClosePositionsSelector = () => {
		setSelectedPositions([]);
		setSelectedPayPeriodPart([]);
		setOpenPositionsSelector(false);
	};

	const preparePositions = (ccId = "") => {
		const positions =
			positionStatuses
				?.filter((item) => item.ccId === ccId)
				?.filter((item) => selectedPositions.includes(item.position))
				?.filter((item) => allowedStatuses.includes(item.status))
				.map((item) => item.position) ?? [];

		if (!partnerPayPeriod) {
			return positions;
		}
		if (!selectedPayPeriodPart.includes(ccId)) {
			return [];
		}
		return positions;
	};

	const handleApproveSelected = async () => {
		const payPeriodParts = partnerPayPeriod
			? [
					{ payPeriodId: payPeriod?._id ?? "", commissionCycleId },
					{ payPeriodId: payPeriod?._id ?? "", commissionCycleId: payPeriod?.nextCCId ?? payPeriod?.previousCCId ?? "" },
			  ]
			: [{ payPeriodId: payPeriod?._id ?? "", commissionCycleId }];
		const mappedPayPeriodParts = payPeriodParts.map((part) => {
			return {
				...part,
				positions: preparePositions(part.commissionCycleId),
			};
		});
		for (const element of mappedPayPeriodParts) {
			if (element.positions.length) {
				approveWashouts(element);
			}
		}
		handleClosePositionsSelector();
	};

	const handleChangePayPeriodPart = (value = "") => {
		if (!value) {
			return;
		}
		setSelectedPayPeriodPart((prevSelected) => {
			if (prevSelected.includes(value)) {
				return prevSelected.filter((item) => item !== value);
			} else {
				return [...prevSelected, value];
			}
		});
	};

	const handleChangeSelectedPositions = (value = "") => {
		if (!value) {
			return;
		}
		setSelectedPositions((prevSelected) => {
			if (prevSelected.includes(value)) {
				return prevSelected.filter((item) => item !== value);
			} else {
				return [...prevSelected, value];
			}
		});
	};

	const handleCheckAll = (value: boolean) => {
		if (value) {
			setSelectedPositions(enabledPositions);
		} else {
			setSelectedPositions([]);
		}
	};

	useEffect(() => {
		if (!selectedPayPeriodPart.length) {
			handleCheckAll(false);
		}
		return () => {};
	}, [selectedPayPeriodPart.length]);

	useEffect(() => {
		setSelectedPositions((prev) => prev.filter((position) => !finalDisabledPositions.includes(position)));
		return () => {};
	}, [finalDisabledPositions.length]);

	useEffect(() => {
		if (!selectedPayPeriodPart.length) {
			const filteredPayPeriodPart =
				compact(
					positionStatuses
						?.filter((item) => selectedPositions.includes(item.position) && allowedStatuses.includes(item.status))
						.map((item) => item.ccId)
				) ?? [];
			setSelectedPayPeriodPart(filteredPayPeriodPart);
		}
		return () => {};
	}, [selectedPositions.length]);

	return (
		<PositionSelectorDialog
			commissionCycleId={commissionCycleId}
			payPeriod={payPeriod}
			partnerPayPeriod={partnerPayPeriod}
			openPositionsSelector={openPositionsSelector}
			closePositionsSelector={handleClosePositionsSelector}
			uniqPositionStatuses={uniqPositionStatuses}
			checkedAll={checkedAll}
			handleCheckAll={handleCheckAll}
			selectedPositions={selectedPositions}
			handleChangeSelectedPositions={handleChangeSelectedPositions}
			selectedPayPeriodPart={selectedPayPeriodPart}
			handleChangePayPeriodPart={handleChangePayPeriodPart}
			disabledPositions={finalDisabledPositions}
			handlePositiveActionButton={handleApproveSelected}
			titleDialog={"Approve Positions"}
			emptyPositionsMessage={"No Position available to Approve All Washouts."}
			positiveActionButtonText={"Approve"}
		/>
	);
}
